// @ts-check
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider } from '@material-ui/core';
import { OrderPropTypes } from 'utils/PropTypesValues';
import { convertStringAmountToNumber } from 'modules/clients/utils';
import ProductItem from '../ProductItem';
import Drawer from '../Drawer';
import Checkbox from '../Checkbox';
import Button from '../Button';

const InventoryDrawer = ({
  classes,
  data,
  open,
  onClose,
  title,
  submitBtnLabel,
  onSubmitBtnClick,
  selectAllCheckboxAvailable,
  separateEachItem,
  itemQtyEditAvailable,
  validateAddQtyBtn,
  showPimSku,
  hidePrice,
  itemPriceInDetails,
  hideQtyInItemDetails,
}) => {
  const filteredItems = React.useMemo(() => {
    const itemsWithoutDeliveryAndGiftWrap = data.orderDetails[0].items.filter(
      (item) => item.serviceName !== 'Delivery' && item.serviceName !== 'Gift Wrap',
    );
    if (separateEachItem) {
      let separatedItems = [];
      itemsWithoutDeliveryAndGiftWrap.forEach((item) => {
        const quantity = Number(item.quantity);
        for (let i = 0; i < quantity; i++) {
          separatedItems = [...separatedItems, { ...item, quantity: '1' }];
        }
      });
      return separatedItems;
    }
    return itemsWithoutDeliveryAndGiftWrap;
  }, [data]);

  const initialItemsValues = React.useMemo(() => {
    return Object.assign(
      {},
      ...filteredItems.map((item, ind) => {
        const itemKey = separateEachItem ? `${item.pimSkuId}_${ind}` : item.pimSkuId;
        return {
          [itemKey]: {
            quantity: convertStringAmountToNumber(item.quantity),
            checked: false,
          },
        };
      }),
    );
  }, [filteredItems]);

  const [savedItemsValues, setSavedItemsValues] = React.useState(initialItemsValues);
  const [allCheckboxesHandler, setAllCheckboxesHandler] = React.useState(false);

  const selectedItems = React.useMemo(() => {
    return Object.keys(savedItemsValues).filter((item) => savedItemsValues[item].checked);
  }, [savedItemsValues]);

  React.useEffect(() => {
    const itemsChecked = Object.keys(savedItemsValues).filter((item) => savedItemsValues[item].checked);
    if (itemsChecked.length >= 1) {
      setAllCheckboxesHandler(true);
      return;
    }
    setAllCheckboxesHandler(false);
  }, [savedItemsValues]);

  const checkboxChangeHandler = (event) => {
    const itemSku = event.target.name;
    const { checked, quantity } = savedItemsValues[itemSku];
    setSavedItemsValues((prevState) => ({
      ...prevState,
      [itemSku]: {
        ...prevState[itemSku],
        checked: !checked,
        quantity: checked ? initialItemsValues[itemSku].quantity : quantity,
      },
    }));
  };

  const selectAllHandler = () => {
    setSavedItemsValues((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((item) => {
        newState[item] = {
          ...newState[item],
          checked: !allCheckboxesHandler,
        };
      });
      return newState;
    });
    setAllCheckboxesHandler((prevState) => !prevState);
  };

  const editQuantity = (item, editType) => {
    const itemSku = item.pimSkuId;
    const prevQuantity = savedItemsValues[itemSku].quantity;
    const newQuantity = editType === 'increase' ? prevQuantity + 1 : prevQuantity - 1;
    setSavedItemsValues((prevState) => ({
      ...prevState,
      [itemSku]: {
        ...prevState[itemSku],
        quantity: newQuantity,
      },
    }));
  };

  const closeDrawer = () => {
    onClose();
    setSavedItemsValues({ ...initialItemsValues });
  };

  const isBtnDisabled = !Object.values(savedItemsValues).find((item) => item.checked === true);

  const mapProductDetails = (item) => {
    const { name, brand, thumbnailUrl, pimSkuId, color, size } = item;
    return {
      brandName: brand,
      pimSkuId,
      name,
      imageUrl: thumbnailUrl,
      currentPrice: convertStringAmountToNumber(item.price.orderPrice),
      color: color !== 'NO COLOR' ? color : null,
      size: size !== 'NO SIZE' ? size : null,
    };
  };

  const renderItem = (item, ind) => {
    const pimSkuId = separateEachItem ? `${item.pimSkuId}_${ind}` : item.pimSkuId;
    const { quantity, checked } = savedItemsValues[pimSkuId];
    return (
      <Fragment key={pimSkuId}>
        <Grid className={classes.item}>
          <Checkbox
            onChange={checkboxChangeHandler}
            checked={checked}
            name={separateEachItem ? `${item.pimSkuId}_${ind}` : item.pimSkuId}
            className={classes.checkbox}
          />
          <ProductItem
            item={mapProductDetails(item)}
            currentQty={quantity}
            editQty={editQuantity}
            miniCartView
            returnItemQty={initialItemsValues[pimSkuId].quantity}
            isReturnItemChecked={checked}
            isDividerHidden
            itemPriceInDetails={itemPriceInDetails}
            hideQtyInItemDetails={hideQtyInItemDetails}
            validateAddQtyBtn={validateAddQtyBtn}
            itemQtyEditAvailable={itemQtyEditAvailable}
            showPimSku={showPimSku}
            hidePrice={hidePrice}
          />
        </Grid>
        <Divider variant="middle" className={classes.divider} />
      </Fragment>
    );
  };

  return (
    <Drawer title={title} open={open} onClose={closeDrawer} data-testid="inventory-drawer">
      <Grid className={classes.container}>
        {selectAllCheckboxAvailable && (
          <Checkbox
            onChange={selectAllHandler}
            checked={allCheckboxesHandler}
            label={!allCheckboxesHandler ? 'Select All' : 'Clear All'}
            className={classes.checkbox}
            indeterminate={allCheckboxesHandler}
          />
        )}
        {filteredItems.map(renderItem)}
        <Button
          label={submitBtnLabel}
          onClick={() => onSubmitBtnClick(selectedItems)}
          disabled={isBtnDisabled}
          data-testid="submit-btn"
        />
      </Grid>
    </Drawer>
  );
};

InventoryDrawer.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  data: OrderPropTypes,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  submitBtnLabel: PropTypes.string,
  onSubmitBtnClick: PropTypes.func,
  selectAllCheckboxAvailable: PropTypes.bool,
  separateEachItem: PropTypes.bool,
  itemQtyEditAvailable: PropTypes.bool,
  validateAddQtyBtn: PropTypes.bool,
  showPimSku: PropTypes.bool,
  hidePrice: PropTypes.bool,
  itemPriceInDetails: PropTypes.bool,
  hideQtyInItemDetails: PropTypes.bool,
};

InventoryDrawer.defaultProps = {
  classes: {},
  data: {},
  open: false,
};

export default InventoryDrawer;
